import type * as React from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import UUID from "../../Utilities/Generic/UUID";

export type PopupId = number | string;

export type PopupType = {
    message: string | React.ReactNode;
    icon: IconProp | React.ReactNode;
    local: boolean;
    classes: Array<string>;
    timeout: number;
    id: PopupId;
    wobble: boolean;
};

class PopupManager {
    protected popups: Array<PopupType>;
    protected listeners: Array<(popups: Array<PopupType>) => void>;
    protected timers: {
        [id: number]: ReturnType<typeof setTimeout>;
        [id: string]: ReturnType<typeof setTimeout>;
    };

    constructor() {
        this.popups = [];
        this.listeners = [];
        this.timers = {};
    }

    protected createPopupID(): PopupId {
        return UUID.generate();
    }

    public addListener(callback: (popups: Array<PopupType>) => void): void {
        this.listeners.push(callback);
    }

    public removeListener(callback: (popups: Array<PopupType>) => void): void {
        const index = this.listeners.indexOf(callback);

        if(index !== -1) {
            this.listeners.splice(index, 1);
        }
    }

    protected emit(): void {
        for(let i = 0, l = this.listeners.length; i < l; i++) {
            this.listeners[i](this.popups);
        }
    }

    public remove(id: PopupId): void {
        if(this.timers[id]) {
            clearTimeout(this.timers[id]);
        }

        this.popups = this.popups.filter(popup => popup.id !== id);
        this.emit();
    }

    protected createTimer(id: PopupId, timeout: number): void {
        this.timers[id] = setTimeout(() => {
            this.remove(id);
        }, timeout);
    }

    public create(popup: Partial<PopupType>): PopupId {
        const defaultPopup: PopupType = {
            message: "No Message Set",
            icon   : "question",
            classes: [],
            timeout: 5000,
            id     : this.createPopupID(),
            local  : false,
            wobble : false,
        };

        const assigned = Object.assign(defaultPopup, popup) as PopupType;

        this.popups.push(assigned);

        if(assigned.timeout !== 0) {
            this.createTimer(assigned.id, assigned.timeout);
        }

        this.emit();

        return assigned.id;
    }

    public success(message: React.ReactNode, local = false, wobble = false, timeout?: number): PopupId {
        const options: Partial<PopupType> = {
            message,
            local,
            icon   : "check",
            classes: [ "popup-success" ],
            wobble,
        };

        if(local && !timeout) {
            options.timeout = 0;
        } else if(typeof timeout !== "undefined") {
            options.timeout = timeout;
        }

        return this.create(options);
    }

    public error(message: React.ReactNode, local = false, wobble = false, timeout?: number): PopupId {
        const options: Partial<PopupType> = {
            message,
            local,
            icon   : "exclamation",
            classes: [ "popup-error" ],
            wobble,
        };

        if(local && !timeout) {
            options.timeout = 0;
        } else if(typeof timeout !== "undefined") {
            options.timeout = timeout;
        }

        return this.create(options);
    }

    public info(message: React.ReactNode, local = false, wobble = false, timeout?: number): PopupId {
        const options: Partial<PopupType> = {
            message,
            local,
            icon   : "info",
            classes: [ "popup-info" ],
            wobble,
        };

        if(local && !timeout) {
            options.timeout = 0;
        } else if(typeof timeout !== "undefined") {
            options.timeout = timeout;
        }

        return this.create(options);
    }

    public warning(message: React.ReactNode, local = false, wobble = false, timeout?: number): PopupId {
        const options: Partial<PopupType> = {
            message,
            local,
            icon   : "exclamation",
            classes: [ "popup-warning" ],
            wobble,
        };

        if(local && !timeout) {
            options.timeout = 0;
        } else if(typeof timeout !== "undefined") {
            options.timeout = timeout;
        }

        return this.create(options);
    }

    public isVisible(id: PopupId): boolean {
        return this.popups.filter(popup => popup.id === id).length !== 0;
    }
}

export default new PopupManager();
